import dayjs, { Dayjs } from "dayjs";

import { useMemo, useState } from "react";

import EventIcon from "@mui/icons-material/Event";
import { Box, IconButton, InputBaseComponentProps } from "@mui/material";
import { DatePickerToolbar, DatePickerToolbarProps } from "@mui/x-date-pickers-pro";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import useEvent from "@/hooks/useEvent";

interface SearchDateProps {
  defaultValue: Dayjs | undefined;
  onChange: (date: Dayjs | null) => void;
  label: string | undefined;
  minDate?: Dayjs;
  maxDate?: Dayjs;
  customToolbar?: React.ReactNode;
  calendarRangeDays?: number;
}

function CustomToolbar(
  props: DatePickerToolbarProps<Dayjs> & {
    customToolbar?: React.ReactNode;
    ownerState?: { wrapperVariant: string };
  }
) {
  const isMobile = props.ownerState?.wrapperVariant === "mobile";

  return (
    <Box className={props.className} sx={{ display: "flex !important", flexDirection: "column" }}>
      {props.customToolbar ? (
        <div
          className="py-[16px] px-[12px] break-words"
          style={{ maxWidth: isMobile ? "320px" : "625px", paddingBottom: isMobile ? 0 : "16px" }}
        >
          {props.customToolbar}
        </div>
      ) : null}
      <DatePickerToolbar {...props} />
    </Box>
  );
}

const SearchDate = ({
  label,
  defaultValue,
  maxDate,
  minDate,
  onChange,
  customToolbar,
  calendarRangeDays = 365,
}: SearchDateProps) => {
  const [open, setOpen] = useState(false);
  const defaultMaxDate = dayjs().add(calendarRangeDays, "day");
  const handleChange = useEvent(
    (value: Dayjs | null, context: { validationError: string | null }) => {
      onChange(context.validationError ? null : value);
    }
  );
  const authenMaxDate =
    defaultMaxDate.isBefore(maxDate || defaultMaxDate) || !maxDate ? defaultMaxDate : maxDate;
  const inputProps = useMemo(() => {
    const data: InputBaseComponentProps = { readOnly: true };

    if (label) {
      data.placeholder = label;
    }

    return data;
  }, [label]);

  return (
    <DatePicker
      minDate={minDate || dayjs()}
      maxDate={authenMaxDate || dayjs().add(1, "year")}
      defaultValue={defaultValue}
      views={["year", "month", "day"]}
      onChange={handleChange}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      open={open}
      format="ddd MM/DD/YYYY"
      slots={{
        toolbar: (props) => <CustomToolbar {...props} customToolbar={customToolbar} />,
      }}
      slotProps={{
        textField: {
          sx: {
            "& .MuiInputAdornment-positionEnd": {
              display: "none",
            },
          },
          fullWidth: true,
          inputProps,
          InputProps: {
            style: { paddingLeft: "4px" },
            startAdornment: (
              <IconButton size="small">
                <EventIcon />
              </IconButton>
            ),
          },
          onClick: () => setOpen(true),
        },
        actionBar: ({ wrapperVariant }) => ({
          actions:
            wrapperVariant === "desktop"
              ? ["today", "clear", "cancel"]
              : ["today", "clear", "cancel", "accept"],
        }),
      }}
    />
  );
};

export default SearchDate;
